import { useMemo, useState } from 'react'

interface TeamMultiSelectProps {
  options: string[]
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

export function TeamMultiSelect({
  options,
  value,
  onChange,
  disabled = false,
}: TeamMultiSelectProps) {
  const [open, setOpen] = useState(false)

  const selected = useMemo(() => new Set(value), [value])
  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.localeCompare(b)),
    [options]
  )

  const toggle = (team: string) => {
    if (disabled) return
    const next = new Set(selected)
    if (next.has(team)) next.delete(team)
    else next.add(team)
    onChange([...next].sort((a, b) => a.localeCompare(b)))
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className="w-full min-h-[2.5rem] px-3 py-2 border border-gray-300 rounded-lg text-sm text-left bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50"
      >
        {value.length === 0 ? (
          <span className="text-gray-400">Select teams…</span>
        ) : (
          <span className="flex flex-wrap gap-1.5">
            {value.map((team) => (
              <span
                key={team}
                className="inline-flex items-center rounded-full bg-teal-50 text-teal-800 px-2 py-0.5 text-xs font-medium"
              >
                {team}
              </span>
            ))}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default"
            aria-label="Close team picker"
            onClick={() => setOpen(false)}
          />
          <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1">
            {sortedOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">No teams available</p>
            ) : (
              sortedOptions.map((team) => {
                const checked = selected.has(team)
                return (
                  <label
                    key={team}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(team)}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    {team}
                  </label>
                )
              })
            )}
          </div>
        </>
      )}
    </div>
  )
}
